
import { createContext, useContext, ReactNode } from "react";
import { BuyerProfile, SavedProperty, SavedSearch } from "@/types/buyer";
import { useAuth } from "./AuthContext";
import { useBuyerData } from "@/hooks/useBuyerData";
import { useLocation } from "react-router-dom";

interface BuyerContextType {
  buyerProfile: BuyerProfile | null;
  savedProperties: SavedProperty[];
  savedSearches: SavedSearch[];
  isBuyer: boolean;
  isLoading: boolean;
  saveProperty: (propertyId: string) => Promise<void>;
  unsaveProperty: (propertyId: string) => Promise<void>;
  isPropertySaved: (propertyId: string) => boolean;
  saveSearch: (name: string, criteria: any) => Promise<void>;
  deleteSearch: (searchId: string) => Promise<void>;
  updateBuyerProfile: (updates: Partial<BuyerProfile>) => Promise<void>;
  refreshBuyerData: () => Promise<void>;
}

const BuyerContext = createContext<BuyerContextType | undefined>(undefined);

export const BuyerProvider = ({ children }: { children: ReactNode }) => {
  const { user, profile } = useAuth();
  const location = useLocation();
  const buyerData = useBuyerData(user?.id || null);

  // Determine if user should be treated as a buyer based on context
  // For dual-profile scenarios, prioritize based on current route
  const isBuyerRoute = location.pathname.startsWith('/buyer');
  const hasOnlyBuyerProfile = Boolean(buyerData.buyerProfile && !profile);
  const hasBothProfiles = Boolean(buyerData.buyerProfile && profile);
  
  // User is a buyer if:
  // 1. They have only a buyer profile, OR
  // 2. They have both profiles but are on a buyer route
  const isBuyer = hasOnlyBuyerProfile || (hasBothProfiles && isBuyerRoute);

  return (
    <BuyerContext.Provider
      value={{
        ...buyerData,
        isBuyer
      }}
    >
      {children}
    </BuyerContext.Provider>
  );
};

export const useBuyer = () => {
  const context = useContext(BuyerContext);
  if (context === undefined) {
    throw new Error("useBuyer must be used within a BuyerProvider");
  }
  return context;
};
